/*
  Save Journey Thunk

  Handles saving journey changes to the backend API.
*/

import { createAsyncThunk } from '@reduxjs/toolkit';
import { typhonAjaxFetch } from '~/helpers/api';
import { reverseTransform } from '../engine';
import type { RootStateWithJourneyEditor } from './types';

interface SaveJourneyParams {
  notes: string;
  setAsActive: boolean;
  typhonDomain: string;
}

interface SaveJourneyResponse {
  version_id: string;
  parent_version_id: string | null;
  active: boolean;
  activated_at: string | null;
  created_at: string;
  archived: boolean;
  notes: string;
  start_node_id: string;
  _embedded: {
    journey: {
      journey_token: string;
      name: string;
      type: string;
    };
  };
}

/**
 * Save journey version to backend
 */
export const saveJourneyVersion = createAsyncThunk<
SaveJourneyResponse,
SaveJourneyParams,
{ state: RootStateWithJourneyEditor }
>('journeyEditor/saveVersion',
  async ({ notes, setAsActive, typhonDomain }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const document = state.journeyEditor.doc;

      if (!document) {
        throw new Error('No document to save');
      }

      // Convert document back to API format
      const apiData = reverseTransform(document);

      // Determine if this is a draft (never activated, not currently active)
      // Matches legacy logic: !journey.active && !journey.activatedAt
      const isDraft = !document.active && !document.activated_at;

      // Prepare save payload
      const payload = {
        ...apiData,
        active: setAsActive,
        notes,
        // If it's a draft: keep existing parent_version (updating draft)
        // If not a draft: set parent_version to current version (creating new version)
        parent_version: isDraft ? document.parent_version_id : document.version,
        is_multi_entity: document.is_multi_entity || false,
        reconciliation_conditions: document.reconciliation_conditions || {},
        terminal_reconciliation_workflow_node_id:
          document.terminal_reconciliation_workflow_node_id || null,
        // Only include version_id if we're updating an existing draft
        // If creating a new version from an active version, omit version_id
        version_id: isDraft ? document.version : undefined,
      };

      // Call save API
      const url = `${typhonDomain}/journeys/${document.token}/versions`;
      const response = await typhonAjaxFetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to save journey');
    }
  },
);

